import { useEffect, type FC } from "react";

import { X } from "lucide-react";

import {
  accountInitials,
  BH_CLASS_FILL,
  BH_CLASS_STROKE,
  type EnrichedMapAccount,
  TIER_HALO,
} from "./portfolioMapVisualHelpers";

interface Props {
  open: boolean;
  accounts: EnrichedMapAccount[];
  onClose: () => void;
  onSelect: (accountId: string) => void;
}

const PortfolioMapOverlapModal: FC<Props> = ({
  open,
  accounts,
  onClose,
  onSelect,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || accounts.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[1600] flex items-center justify-center bg-black/35 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${accounts.length} accounts at this map position`}
        className="w-full max-w-[360px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <div className="text-[13px] font-bold text-gray-900">
              Overlapping accounts
            </div>
            <div className="text-[11px] text-gray-500">
              {accounts.length} accounts share this Fit / score position. Pick
              one to focus.
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="max-h-[360px] overflow-y-auto py-1">
          {accounts.map(account => {
            const stroke =
              BH_CLASS_STROKE[account.bh_class] ?? BH_CLASS_STROKE.no_data;
            const fill =
              BH_CLASS_FILL[account.bh_class] ?? BH_CLASS_FILL.no_data;
            const halo = account.top_signal_tier
              ? TIER_HALO[account.top_signal_tier]
              : undefined;
            return (
              <li key={account.account_id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(account.account_id);
                    onClose();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-brand-faint"
                >
                  <span className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center">
                    {halo && (
                      <span
                        className="absolute inset-[-2px] rounded-full"
                        style={{ boxShadow: `0 0 0 2px ${halo}` }}
                        aria-hidden
                      />
                    )}
                    <span
                      className="flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-extrabold"
                      style={{
                        background: fill,
                        border: `1.5px solid ${stroke}`,
                        color: stroke,
                      }}
                    >
                      {accountInitials(account.account_name)}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-gray-900">
                      {account.account_name}
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      Fit {account.fit != null ? Math.round(account.fit) : "—"}
                      {" · "}
                      BH {account.bh != null ? Math.round(account.bh) : "—"}
                      {account.top_signal_tier
                        ? ` · ${account.top_signal_tier}`
                        : ""}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PortfolioMapOverlapModal;
